import React, { useEffect, useMemo, useState as useStateHook, useRef } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector as useSelectorHook } from 'react-redux';
import { useFeatureValue } from '@growthbook/growthbook-react';

import { DeviceTypes } from '@gumtree/shared/src/types/client-data';
import {
    setCategoryBrpEntryCookie,
    type CategoryBrpEntry,
} from '@gumtree/shared/src/util/category-brp-entry-cookie';
import { getCurrentBreakpoint } from '@gumtree/shared/src/util/breakpoints-service';
import { trackGA4PreNav, trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';
import { colors, zIndexes } from '@gumtree/ui-library/src/base/theme';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';

import { getL1CategoryIdBySeoName } from '@gumtree/shared/src/util/category-mapping';
import { ExperimentVariant, GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import type { ShellState, ShellUseDispatch } from '../reducers/common';
import type { State as PageState } from '../reducers/page';

import {
    isCategoryBrpEntryPath,
    redirectCategoryLandingPageEntriesToBrp,
    type CategoryBrpEntryExperiments,
} from './reducer';

import NavItemList from './category-nav-ribbon-item-list';
import MhcMobileCategoryNav from './mhc-mobile-category-nav';
import SubNavPanel from './sub-nav-panel';
import { MhcDesktopSubNavContext } from './sub-nav-panel-section-item';
import { trackAndNavigate, trackV2 } from '../common/common-actions';

const MHC_DESKTOP_INLINE_MEDIA_QUERY = '@media (min-width: 1025px)';

const useDelayedExperimentViewedAlias = (experimentId: string, variationId: string) => {
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            trackGA4Event({
                event: 'experiment_viewed',
                experimentId: `${experimentId}_DELAYED_2S`,
                variationId,
            });
        }, 2000);

        return () => window.clearTimeout(timeoutId);
    }, [experimentId, variationId]);
};

const Overlay = styled.div`
    z-index: ${zIndexes.low - 1};
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${colors.black};
    opacity: 0.5;
`;

const NavItemListContainer = styled.div<{
    isActive: boolean;
    isHoverDisabled?: boolean;
    isMhcDesktop?: boolean;
    isMhcDesktopLoggedIn?: boolean;
}>`
    position: relative;
    background-color: ${colors.barkDark};
    background-color: ${colors.white};
    border-bottom: 1px solid ${colors.bark20};

    ${(p) =>
        p.isActive &&
        `
            z-index: ${zIndexes.high};
            background-color: ${colors.branch10};
            border-bottom: initial;
        `}

    ${(p) =>
        p.isHoverDisabled &&
        `
            li:hover {
                cursor: default;
            }

            li:hover::before,
            li:hover::after {
                content: none;
            }

            li a:hover {
                text-decoration: none;
            }

            li:hover a span::after,
            [data-active='true'] a span::after {
                content: none;
            }
        `}

    ${(p) =>
        p.isMhcDesktop &&
        `
            border-bottom: 1px solid #f0f4fa;
            background-color: ${colors.white};
            height: 64px;

            .container {
                max-width: none;
                box-sizing: border-box;
                padding: 0 24px;

                ${MHC_DESKTOP_INLINE_MEDIA_QUERY} {
                    max-width: 1350px;
                    margin: 0 auto;
                    padding: 0 8px;
                }
            }

            ul {
                height: 64px;
                justify-content: space-between;
                gap: 24px;
                padding: 0;
                margin: 0;
                overflow: visible;
            }

            li {
                flex: 1;
                width: auto !important;
                text-align: center;
                border-left: none !important;

                & + li {
                    border-left: none !important;
                }
            }

            li a {
                font-size: 20px;
                font-weight: 400;
                line-height: 30px;
                color: #101824;
                padding-top: 17px;
                padding-left: 0;
                padding-right: 0;

                &:hover,
                &:focus {
                    text-decoration: none;
                }
            }

            li a span {
                position: relative;
                display: inline-block;
            }

            li:hover a span::after,
            [data-active='true'] a span::after {
                content: '';
                position: absolute;
                z-index: 4;
                left: 50%;
                bottom: -17px;
                width: 20px;
                height: 3px;
                border-radius: 2px;
                background: var(--color-text-default-1, #101824);
                transform: translateX(-50%);
            }

            ${p.isMhcDesktopLoggedIn ? 'li a { font-weight: 500; }' : ''}

            [class*="Fade"] {
                display: none !important;
            }
        `}
`;

const FIRST_TIME_PANEL_EXPAND_DELAY = 200;

interface Props {
    onMouseLeave?: EventListener;
    useState?: typeof useStateHook;
    useSelector?: typeof useSelectorHook;
    className?: string;
    isHoverDisabled?: boolean;
    isMhcDesktop?: boolean;
}

interface NavigationRibbonContentProps extends Props {
    brpEntryExperiments: CategoryBrpEntryExperiments;
    isAppView?: boolean;
}

type CategoryNavRibbonState = ShellState & { page?: Pick<PageState, 'appViewDevice'> };

const NavigationRibbonContent = ({
    onMouseLeave,
    useState = useStateHook,
    useSelector = useSelectorHook,
    isHoverDisabled = false,
    isMhcDesktop,
    brpEntryExperiments,
    isAppView = false,
    ...props
}: NavigationRibbonContentProps) => {
    const dispatch = useDispatch() as ShellUseDispatch;

    const categoryNavState = useSelector(({ categoryNav }: ShellState) => categoryNav);

    const [isShowHoverUnderline, setIsShowHoverUnderline] = useState<boolean>(false);
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [activeNavItemIndex, setActiveNavItemIndex] = useState<number | null>(null);
    const delayedPanelOpenId = useRef<number>();
    const [firstHoverTime, setFirstHoverTime] = useState<number | null>(null);

    const { category, device, pageType, userLoggedIn } = useSelector(
        ({ baseConfig, userData }: CategoryNavRibbonState) => ({
            category: baseConfig.category,
            device: baseConfig.device,
            pageType: baseConfig.pageType,
            userLoggedIn: Boolean(userData?.userLoggedIn),
        })
    );
    const mhcVariant = useFeatureValue(GrowthBookFeature.MHC, ExperimentVariant.VARIANT_A);

    const { items = [] } = categoryNavState;

    const isMobile = device?.type === DeviceTypes.MOBILE;
    const isDesktop = device?.type === DeviceTypes.DESKTOP;
    const navItems = useMemo(
        () => redirectCategoryLandingPageEntriesToBrp(items, brpEntryExperiments),
        [items, brpEntryExperiments.cars, brpEntryExperiments.services]
    );
    const navItemListContainerRef = useRef(null!);

    React.useEffect(() => {
        if (!isHoverDisabled) {
            return;
        }

        clearTimeout(delayedPanelOpenId.current);
        delayedPanelOpenId.current = undefined;
        setActiveCategory(null);
        setActiveNavItemIndex(null);
        setIsShowHoverUnderline(false);
        setFirstHoverTime(null);
    }, [isHoverDisabled]);

    if (!categoryNavState.display) {
        return <></>;
    }

    const shouldShowMhcMobileNav =
        isMobile &&
        (mhcVariant === ExperimentVariant.VARIANT_B ||
            (mhcVariant === ExperimentVariant.VARIANT_C && pageType === 'Homepage'));

    if (shouldShowMhcMobileNav) {
        return (
            <MhcMobileCategoryNav
                categoryItems={items}
                brpEntryExperiments={brpEntryExperiments}
                pageType={pageType}
                selectedCategorySeoName={category?.l1Category}
                variant={mhcVariant as ExperimentVariant}
            />
        );
    }

    const openSubNavPanelNow = (seoName) => {
        setActiveCategory(seoName);
        setFirstHoverTime(null);
        const eventLabel = navItems.find((ele) => ele.seoName === seoName)?.name;

        requestAnimationFrame(() => {
            setTimeout(() => {
                trackV2('NavL1CategoryHover', eventLabel);
                trackGA4Event<GA4.NavigationMenuExpanded>({
                    event: 'navigation_menu_expanded',
                    navigationCategory: eventLabel ?? seoName,
                });
            }, 0);
        });
    };

    const openSubNavPanel = (seoName) => {
        if (!activeCategory) {
            if (!firstHoverTime) {
                setFirstHoverTime(Date.now());
                delayedPanelOpenId.current = setTimeout(() => {
                    openSubNavPanelNow(seoName);
                }, FIRST_TIME_PANEL_EXPAND_DELAY) as unknown as number;
            } else {
                clearTimeout(delayedPanelOpenId.current);
                const timeStayedHovering = Date.now() - firstHoverTime;
                delayedPanelOpenId.current = setTimeout(() => {
                    openSubNavPanelNow(seoName);
                }, FIRST_TIME_PANEL_EXPAND_DELAY - timeStayedHovering) as unknown as number;
            }
        } else {
            openSubNavPanelNow(seoName);
        }
    };

    const closeSubNavPanel = () => {
        const categoryToTrack = activeCategory;

        setActiveCategory(null);
        setFirstHoverTime(null);

        if (categoryToTrack) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    trackGA4Event<GA4.NavigationMenuMinimised>({
                        event: 'navigation_menu_minimised',
                    });
                }, 0);
            });
        }
    };

    const navItemMouseEnterHandler = (_e, { seoName, index }) => {
        if (isHoverDisabled) {
            return;
        }

        setActiveNavItemIndex(index);
        setIsShowHoverUnderline(true);

        if (
            // only expand for desktop device except touch devices, since this event is also triggerd at the same time by touch device tap
            ['desktop'].includes(device?.type as DeviceTypes) &&
            getCurrentBreakpoint()?.tshirt !== 'xs'
        ) {
            openSubNavPanel(seoName);
        }
    };

    const navItemClickHandler = async (e, { seoName, path, name }) => {
        let brpEntry: CategoryBrpEntry | undefined;
        if (seoName === 'cars-vans-motorbikes') {
            brpEntry = brpEntryExperiments.cars
                ? isDesktop
                    ? 'cars:desktop'
                    : isMobile
                    ? 'cars:mobile'
                    : undefined
                : undefined;
        } else if (seoName === 'business-services') {
            brpEntry = brpEntryExperiments.services
                ? isDesktop
                    ? 'services:desktop'
                    : isMobile
                    ? 'services:mobile'
                    : undefined
                : undefined;
        }

        const nextActiveCategory = seoName;
        const isMobileCarsSrpEntry =
            isMobile && pageType !== 'Homepage' && seoName === 'cars-vans-motorbikes';

        if (isMobileCarsSrpEntry) {
            e.preventDefault();
            openSubNavPanel(nextActiveCategory);
            return;
        }

        if (brpEntry && !isMobileCarsSrpEntry) {
            // Services entry cookie preserves legacy hotspot state; BFF keeps the root entry stable.
            setCategoryBrpEntryCookie(brpEntry);
            trackGA4PreNav<GA4.ClickNavigationMenu>({
                event: 'click_navigation_menu',
                linkDomain: getDomainFromUrl(path),
                linkText: name,
                linkURL: path,
                navigationCategory: seoName as any,
                navigationCategoryId: getL1CategoryIdBySeoName(seoName),
                navigationHeader: undefined,
                navigationHierarchy: 'category',
            });
            return;
        }

        if (
            isMobile &&
            (seoName === 'business-services' ||
                (pageType === 'Homepage' && seoName === 'cars-vans-motorbikes'))
        ) {
            if (seoName === 'business-services' && !isAppView) {
                trackGA4PreNav<GA4.ClickNavigationMenu>({
                    event: 'click_navigation_menu',
                    linkDomain: getDomainFromUrl(path),
                    linkText: name,
                    linkURL: path,
                    navigationCategory: seoName as any,
                    navigationCategoryId: getL1CategoryIdBySeoName(seoName),
                    navigationHeader: undefined,
                    navigationHierarchy: 'category',
                });
            }
            return;
        }

        if (nextActiveCategory === activeCategory) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    trackGA4PreNav<GA4.ClickNavigationMenu>({
                        event: 'click_navigation_menu',
                        linkDomain: getDomainFromUrl(path),
                        linkText: name,
                        linkURL: path,
                        navigationCategory: seoName as any,
                        navigationCategoryId: getL1CategoryIdBySeoName(seoName),
                        navigationHeader: undefined,
                        navigationHierarchy: 'category',
                    });

                    dispatch(trackAndNavigate('NavL1CategoryClick', path, name));
                }, 0);
            });
            return;
        }

        e.preventDefault();
        openSubNavPanel(nextActiveCategory);
    };

    const cancelPreviousOpenSubNavPanel = () => {
        clearTimeout(delayedPanelOpenId.current);
        delayedPanelOpenId.current = undefined;
    };

    const mouseLeaveHandler = (e) => {
        if (getCurrentBreakpoint()?.tshirt !== 'xs') {
            closeSubNavPanel();
            cancelPreviousOpenSubNavPanel();
        }
        setIsShowHoverUnderline(false);
        onMouseLeave && onMouseLeave(e);
        setActiveNavItemIndex(null);
    };

    const subNavPanelOnCloseHandler = () => {
        closeSubNavPanel();
        setIsShowHoverUnderline(false);
    };

    return (
        <MhcDesktopSubNavContext.Provider value={Boolean(isMhcDesktop)}>
            <div
                role="menu"
                tabIndex={0}
                onMouseLeave={mouseLeaveHandler}
                {...props}
                {...qaAttribute('category-nav-ribbon')}
            >
                <NavItemListContainer
                    ref={navItemListContainerRef}
                    isActive={!isHoverDisabled && activeCategory !== null}
                    isHoverDisabled={isHoverDisabled}
                    isMhcDesktop={isMhcDesktop}
                    isMhcDesktopLoggedIn={isMhcDesktop && userLoggedIn}
                >
                    <NavItemList
                        items={navItems}
                        showHoverUnderline={!isHoverDisabled && isShowHoverUnderline}
                        onItemClick={navItemClickHandler}
                        onItemMouseEnter={navItemMouseEnterHandler}
                        activeItemIndex={isHoverDisabled ? null : activeNavItemIndex}
                    />
                </NavItemListContainer>
                {navItems.map((item) => (
                    <SubNavPanel
                        title={item.name}
                        seoName={item.seoName}
                        isActive={!isHoverDisabled && item.seoName === activeCategory}
                        key={`cat-nav-ribbon-panel-${item.seoName}`}
                        items={item.subitems}
                        brpEntry={
                            brpEntryExperiments.cars &&
                            item.seoName === 'cars-vans-motorbikes' &&
                            isCategoryBrpEntryPath(item.path)
                                ? isDesktop
                                    ? 'cars:desktop'
                                    : isMobile
                                    ? 'cars:mobile'
                                    : undefined
                                : undefined
                        }
                        onClose={subNavPanelOnCloseHandler}
                    />
                ))}
            </div>
            <Overlay style={{ display: !isHoverDisabled && activeCategory ? 'block' : 'none' }} />
        </MhcDesktopSubNavContext.Provider>
    );
};

const MobileNavigationRibbon = (props: Props) => {
    const clpM2Variant = useFeatureValue(GrowthBookFeature.CLP_M2, ExperimentVariant.VARIANT_A);
    const slpMVariant = useFeatureValue(GrowthBookFeature.SLP_M, ExperimentVariant.VARIANT_A);
    useDelayedExperimentViewedAlias(GrowthBookFeature.SLP_M, slpMVariant);

    return (
        <NavigationRibbonContent
            {...props}
            brpEntryExperiments={{
                cars: clpM2Variant === ExperimentVariant.VARIANT_B,
                services: slpMVariant === ExperimentVariant.VARIANT_B,
            }}
        />
    );
};

const DesktopNavigationRibbon = (props: Props) => {
    const clpPc2Variant = useFeatureValue(GrowthBookFeature.CLP_PC2, ExperimentVariant.VARIANT_A);
    const slpPcVariant = useFeatureValue(GrowthBookFeature.SLP_PC, ExperimentVariant.VARIANT_A);
    useDelayedExperimentViewedAlias(GrowthBookFeature.SLP_PC, slpPcVariant);

    return (
        <NavigationRibbonContent
            {...props}
            brpEntryExperiments={{
                cars: clpPc2Variant === ExperimentVariant.VARIANT_B,
                services: slpPcVariant === ExperimentVariant.VARIANT_B,
            }}
        />
    );
};

const NavigationRibbon = ({ useSelector = useSelectorHook, ...props }: Props) => {
    const { deviceType, isAppView } = useSelector(
        ({ baseConfig, page }: CategoryNavRibbonState) => ({
            deviceType: baseConfig.device?.type,
            isAppView: Boolean(page?.appViewDevice),
        })
    );

    if (isAppView || (deviceType !== DeviceTypes.MOBILE && deviceType !== DeviceTypes.DESKTOP)) {
        return (
            <NavigationRibbonContent
                {...props}
                useSelector={useSelector}
                brpEntryExperiments={{}}
                isAppView
            />
        );
    }

    return deviceType === DeviceTypes.MOBILE ? (
        <MobileNavigationRibbon {...props} useSelector={useSelector} />
    ) : (
        <DesktopNavigationRibbon {...props} useSelector={useSelector} />
    );
};

export default NavigationRibbon;
